import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { Plus, Printer } from 'lucide-react';


import { printDeliveryNote } from '../lib/documents';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;


export default function DeliveryNotes() {
  const [deliveryNotes, setDeliveryNotes] = useState([]);
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    order_id: '',
    customer_id: '',
    invoice_id: '',
    items: [],
    delivery_address: '',
    delivery_date: '',
    vehicle_number: '',
    driver_name: '',
    notes: ''
  });

  useEffect(() => {
    fetchDeliveryNotes();
    fetchOrders();
    fetchCustomers();
    fetchInvoices();
  }, []);

  const fetchDeliveryNotes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/delivery-notes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDeliveryNotes(response.data);
    } catch (error) {
      toast.error('Failed to fetch delivery notes');
    }
  };

  const fetchOrders = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(response.data);
    } catch (error) {
      console.error('Failed to fetch orders');
    }
  };

  const fetchCustomers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/customers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomers(response.data);
    } catch (error) {
      console.error('Failed to fetch customers');
    }
  };

  const fetchInvoices = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/invoices`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInvoices(response.data);
    } catch (error) {
      console.error('Failed to fetch invoices');
    }
  };

  const handleOrderSelect = (orderId) => {
    const order = orders.find(o => o.id === orderId);
    if (order) {
      const customer = customers.find(c => c.id === order.customer_id);
      setFormData({
        ...formData,
        order_id: orderId,
        customer_id: order.customer_id,
        items: order.items,
        delivery_address: customer?.address || ''
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/delivery-notes`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Delivery note created successfully');
      setOpen(false);
      resetForm();
      fetchDeliveryNotes();
    } catch (error) {
      toast.error('Failed to create delivery note');
    }
  };

  const resetForm = () => {
    setFormData({
      order_id: '',
      customer_id: '',
      invoice_id: '',
      items: [],
      delivery_address: '',
      delivery_date: '',
      vehicle_number: '',
      driver_name: '',
      notes: ''
    });
  };

  const getCustomerName = (noteOrId) => {
    if (noteOrId && typeof noteOrId === 'object') {
      const n = noteOrId;
      if (n.customer_details?.name) return n.customer_details.name;
      const c = customers.find((c) => c.id === n.customer_id);
      return c ? c.name : 'Unknown';
    }
    const customer = customers.find((c) => c.id === noteOrId);
    return customer ? customer.name : 'Unknown';
  };



  return (
    <div data-testid="delivery-notes-page">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Delivery Notes</h1>
          <p className="text-slate-600 mt-1">Track and manage deliveries</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-delivery-note-button" onClick={resetForm}>
              <Plus className="mr-2" size={16} />
              Create Delivery Note
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="delivery-note-dialog">
            <DialogHeader>
              <DialogTitle>Create Delivery Note</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="order">Select Order *</Label>
                <Select value={formData.order_id} onValueChange={handleOrderSelect}>
                  <SelectTrigger data-testid="order-select">
                    <SelectValue placeholder="Select order" />
                  </SelectTrigger>
                  <SelectContent>
                    {orders.map((order) => (
                      <SelectItem key={order.id} value={order.id}>
                        {order.order_number}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="delivery_address">Delivery Address *</Label>
                <Input
                  id="delivery_address"
                  data-testid="delivery-address-input"
                  value={formData.delivery_address}
                  onChange={(e) => setFormData({ ...formData, delivery_address: e.target.value })}
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="delivery_date">Delivery Date</Label>
                  <Input
                    id="delivery_date"
                    data-testid="delivery-date-input"
                    type="date"
                    value={formData.delivery_date}
                    onChange={(e) => setFormData({ ...formData, delivery_date: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="vehicle_number">Vehicle Number</Label>
                  <Input
                    id="vehicle_number"
                    data-testid="vehicle-number-input"
                    value={formData.vehicle_number}
                    onChange={(e) => setFormData({ ...formData, vehicle_number: e.target.value })}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="driver_name">Driver Name</Label>
                <Input
                  id="driver_name"
                  data-testid="driver-name-input"
                  value={formData.driver_name}
                  onChange={(e) => setFormData({ ...formData, driver_name: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="notes">Notes</Label>
                <Input
                  id="notes"
                  data-testid="notes-input"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                />
              </div>

              <Button data-testid="save-delivery-note-button" type="submit" className="w-full">
                Create Delivery Note
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white rounded-lg border border-slate-200">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Delivery Number</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Delivery Address</TableHead>
              <TableHead>Delivery Date</TableHead>
              <TableHead>Vehicle</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {deliveryNotes.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-slate-500">
                  No delivery notes found. Create your first delivery note.
                </TableCell>
              </TableRow>
            ) : (
              deliveryNotes.map((note) => (
                <TableRow key={note.id} data-testid={`delivery-note-row-${note.id}`}>
                  <TableCell className="font-medium">{note.delivery_number}</TableCell>
                  <TableCell>{getCustomerName(note)}</TableCell>
                  <TableCell>{note.delivery_address}</TableCell>
                  <TableCell>{note.delivery_date || 'Not set'}</TableCell>
                  <TableCell>{note.vehicle_number || '-'}</TableCell>
                  <TableCell className="text-right">
                    <Button
                      data-testid={`print-delivery-note-${note.id}`}
                      variant="ghost"
                      size="icon"
                      onClick={() => printDeliveryNote(note, { customers })}
                    >
                      <Printer size={16} />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
